/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import useBreakpoint from '../hooks/useBreakpoint';

const H1 = ({ children, ...props }) => <h1 {...props}>{children}</h1>;
const H2 = ({ children, ...props }) => <h2 {...props}>{children}</h2>;
const H3 = ({ children, ...props }) => <h3 {...props}>{children}</h3>;
const H4 = ({ children, ...props }) => <h4 {...props}>{children}</h4>;
const H5 = ({ children, ...props }) => <h5 {...props}>{children}</h5>;
const H6 = ({ children, ...props }) => <h6 {...props}>{children}</h6>;

const HEADER_MAPPER = {
  h1: H1,
  h2: H2,
  h3: H3,
  h4: H4,
  h5: H5,
  h6: H6,
};
/**
 * @typedef {('h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6')} Breakpoint
 *
 * @typedef {{
 *  xs: Breakpoint,
 *  sm: Breakpoint,
 *  lg: Breakpoint,
 *  xl: Breakpoint,
 *  xxl: Breakpoint,
 *  text: string
 * } ResponsiveHeaderParams
 * }
 */
/**
 *
 * @param {ResponsiveHeaderParams} params
 * @returns
 */
const ResponsiveHeader = ({
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  text = '',
  children = null,
  className
}) => {

  const breakpoint = useBreakpoint();
  const XsHeader = HEADER_MAPPER[xs];
  const SmHeader = sm ? HEADER_MAPPER[sm] : XsHeader;
  const MdHeader = md ? HEADER_MAPPER[md] : SmHeader;
  const LgHeader = lg ? HEADER_MAPPER[lg] : MdHeader;
  const XlHeader = xl ? HEADER_MAPPER[xl] : LgHeader;
  const XxlHeader = xxl ? HEADER_MAPPER[xxl] : XlHeader;

  const content = text || children;

  switch (breakpoint) {
    case 'xs': return <XsHeader className={className}>{content}</XsHeader>;
    case 'sm': return <SmHeader className={className}>{content}</SmHeader>;
    case 'md': return <MdHeader className={className}>{content}</MdHeader>;
    case 'lg': return <LgHeader className={className}>{content}</LgHeader>;
    case 'xl': return <XlHeader className={className}>{content}</XlHeader>;
    case 'xxl': return <XxlHeader className={className}>{content}</XxlHeader>;
    default: return null;
  }

};

ResponsiveHeader.propTypes = {
  xs: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  sm: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  md: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  lg: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  xl: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  xxl: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  text: PropTypes.string,
  children: PropTypes.any,
  className: PropTypes.any,
};

ResponsiveHeader.defaultProps = {
  xs: 'h3', // Header to be rendered for xs screen size and above
  sm: null, // Header to be rendered for sm screen size and above
  lg: null, // Header to be rendered for lg screen size and above
  xl: null, // Header to be rendered for xl screen size and above
  xxl: null, // Header to be rendered for xxl screen size and above
  text: '',
  children: null,
  className: null,
};

export default React.memo(ResponsiveHeader);